import {
  communityReportCopy,
  countCommunityReportCopy,
} from "../../community-report-copy.js";
import { loadMapboxGl, mapboxToken } from "../mapbox-client.js";
import {
  COUNT_SHADING_FILL_STYLE,
  countShadingFillColorExpression,
  DEFAULT_COUNT_SHADING_THRESHOLDS,
  validateCountShadingThresholds,
} from "../count-shading.js";

export const COUNTY_SOURCE_ID = "county-map-counties";
export const COUNTY_FILL_LAYER_ID = "county-map-fills";
export const COUNTY_BOUNDARY_LAYER_ID = "county-map-boundaries";
export const COUNTY_LABEL_LAYER_ID = "county-map-labels";

export const COUNTY_LAYER_ORDER = Object.freeze([
  COUNTY_FILL_LAYER_ID,
  COUNTY_BOUNDARY_LAYER_ID,
  COUNTY_LABEL_LAYER_ID,
]);

const ARIZONA_MAP_BOUNDS = Object.freeze([
  Object.freeze([-114.9, 31.15]),
  Object.freeze([-109, 37.1]),
]);
const COUNTY_NO_DATA_COLORS = Object.freeze({
  light: "#CBD5E1",
  dark: "#475569",
});

export function countyFillColorExpression(
  thresholds = DEFAULT_COUNT_SHADING_THRESHOLDS,
  theme = "light",
) {
  validateCountShadingThresholds(thresholds);
  return [
    "case",
    ["==", ["get", "hasCountyData"], false],
    COUNTY_NO_DATA_COLORS[theme] || COUNTY_NO_DATA_COLORS.light,
    countShadingFillColorExpression(thresholds),
  ];
}

export function countyLayerDefinitions({
  thresholds = DEFAULT_COUNT_SHADING_THRESHOLDS,
  theme = "light",
} = {}) {
  const dark = theme === "dark";
  return [
    {
      id: COUNTY_FILL_LAYER_ID,
      type: "fill",
      source: COUNTY_SOURCE_ID,
      paint: {
        "fill-color": countyFillColorExpression(thresholds, theme),
        "fill-opacity": [
          "case",
          ["==", ["get", "hasCountyData"], false],
          0.48,
          COUNT_SHADING_FILL_STYLE.opacity,
        ],
      },
    },
    {
      id: COUNTY_BOUNDARY_LAYER_ID,
      type: "line",
      source: COUNTY_SOURCE_ID,
      paint: {
        "line-color": dark ? "#E2E8F0" : "#FFFFFF",
        "line-opacity": 0.9,
        "line-width": 1.35,
      },
    },
    {
      id: COUNTY_LABEL_LAYER_ID,
      type: "symbol",
      source: COUNTY_SOURCE_ID,
      layout: {
        "text-field": ["get", "countyLabel"],
        "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 5, 10, 8, 13],
        "text-allow-overlap": false,
      },
      paint: {
        "text-color": dark ? "#F8FAFC" : "#172033",
        "text-halo-color": dark ? "#0F172A" : "#FFFFFF",
        "text-halo-width": 1.25,
        "text-halo-blur": 0.25,
      },
    },
  ];
}

export function countyPopupMarkup(
  details,
  { language = "EN" } = {},
) {
  const countyName = escapeCountyHtml(details?.countyName || "");
  const body = details?.hasCountyData
    ? [
        countCommunityReportCopy(
          language,
          "county_sick_report_one",
          "county_sick_report_many",
          details.sickCount,
          { count: formatCountyNumber(details.sickCount, language) },
        ),
        countCommunityReportCopy(
          language,
          "county_total_report_one",
          "county_total_report_many",
          details.totalCount,
          { count: formatCountyNumber(details.totalCount, language) },
        ),
      ]
    : [communityReportCopy(language, "county_no_data")];

  return `
    <div class="county-map-popup">
      <strong>${countyName}</strong>
      <span class="county-map-popup-sample">${escapeCountyHtml(communityReportCopy(language, "county_sample_popup_note"))}</span>
      ${body.map((line) => `<span>${escapeCountyHtml(line)}</span>`).join("")}
    </div>
  `;
}

export function formatCountyNumber(value, language = "EN") {
  return new Intl.NumberFormat(language === "ES" ? "es-US" : "en-US").format(value);
}

export function countyDetailsFromFeature(feature) {
  const properties = feature?.properties || {};
  const hasCountyData = properties.hasCountyData === true || properties.hasCountyData === "true";
  return Object.freeze({
    countyName: String(properties.countyName || properties.NAME || ""),
    hasCountyData,
    sickCount: hasCountyData ? numericFeatureCount(properties.sickCount) : 0,
    totalCount: hasCountyData ? numericFeatureCount(properties.totalCount) : 0,
  });
}

export async function initializeCountyMap({
  element,
  featureCollection,
  thresholds = DEFAULT_COUNT_SHADING_THRESHOLDS,
  theme = "light",
  language = "EN",
  mapboxLoader = loadMapboxGl,
  tokenProvider = mapboxToken,
} = {}) {
  if (!element) throw new TypeError("A county map element is required.");
  validateCountShadingThresholds(thresholds);
  const token = tokenProvider();
  if (!token) throw new Error(communityReportCopy(language, "county_mapbox_token"));

  await mapboxLoader();
  if (!window.mapboxgl?.Map || !window.mapboxgl?.Popup) {
    throw new Error(communityReportCopy(language, "county_map_unavailable"));
  }
  window.mapboxgl.accessToken = token;
  element.innerHTML = "";

  const map = new window.mapboxgl.Map({
    container: element.id,
    style: theme === "dark"
      ? "mapbox://styles/mapbox/dark-v11"
      : "mapbox://styles/mapbox/light-v11",
    projection: "mercator",
    bounds: ARIZONA_MAP_BOUNDS,
    fitBoundsOptions: { padding: 42, duration: 0 },
    maxBounds: [[-117.3, 29.8], [-106.7, 38.4]],
    attributionControl: false,
  });
  map.addControl(new window.mapboxgl.AttributionControl({ compact: true }), "bottom-right");

  await waitForInitialStyle(map);
  map.addSource(COUNTY_SOURCE_ID, { type: "geojson", data: featureCollection });
  countyLayerDefinitions({ thresholds, theme }).forEach((layer) => map.addLayer(layer));

  let currentLanguage = language;
  let hoverPopup = null;
  let hoverDetails = null;
  let hoverLngLat = null;
  let pinnedPopup = null;
  let pinnedDetails = null;
  let pinnedLngLat = null;
  let destroyed = false;
  const listeners = [];

  const addListener = (event, layerOrHandler, maybeHandler) => {
    if (maybeHandler) {
      map.on(event, layerOrHandler, maybeHandler);
      listeners.push([event, layerOrHandler, maybeHandler]);
      return;
    }
    map.on(event, layerOrHandler);
    listeners.push([event, layerOrHandler]);
  };

  const popupOptions = () => ({ language: currentLanguage });
  const removeHoverPopup = () => {
    hoverPopup?.remove();
    hoverPopup = null;
    hoverDetails = null;
    hoverLngLat = null;
  };
  const removePinnedPopup = () => {
    pinnedPopup?.remove();
    pinnedPopup = null;
    pinnedDetails = null;
    pinnedLngLat = null;
  };
  const showHoverPopup = (event) => {
    if (pinnedPopup || !event.features?.[0]) return;
    hoverDetails = countyDetailsFromFeature(event.features[0]);
    hoverLngLat = event.lngLat;
    if (!hoverPopup) {
      hoverPopup = new window.mapboxgl.Popup({ closeButton: false, closeOnClick: false });
    }
    hoverPopup
      .setLngLat(hoverLngLat)
      .setHTML(countyPopupMarkup(hoverDetails, popupOptions()))
      .addTo(map);
  };
  const pinCountyPopup = (event) => {
    if (!event.features?.[0]) return;
    removeHoverPopup();
    removePinnedPopup();
    pinnedDetails = countyDetailsFromFeature(event.features[0]);
    pinnedLngLat = event.lngLat;
    const popup = new window.mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat(pinnedLngLat)
      .setHTML(countyPopupMarkup(pinnedDetails, popupOptions()))
      .addTo(map);
    pinnedPopup = popup;
    popup.on?.("close", () => {
      if (pinnedPopup === popup) {
        pinnedPopup = null;
        pinnedDetails = null;
        pinnedLngLat = null;
      }
    });
  };
  const clearPinnedOnBackground = (event) => {
    const features = map.queryRenderedFeatures?.(event.point, { layers: [COUNTY_FILL_LAYER_ID] }) || [];
    if (features.length === 0) removePinnedPopup();
  };
  const setPointer = () => { map.getCanvas().style.cursor = "pointer"; };
  const clearPointer = () => {
    map.getCanvas().style.cursor = "";
    removeHoverPopup();
  };

  addListener("mouseenter", COUNTY_FILL_LAYER_ID, setPointer);
  addListener("mousemove", COUNTY_FILL_LAYER_ID, showHoverPopup);
  addListener("mouseleave", COUNTY_FILL_LAYER_ID, clearPointer);
  addListener("click", COUNTY_FILL_LAYER_ID, pinCountyPopup);
  addListener("click", clearPinnedOnBackground);

  return Object.freeze({
    get map() {
      return map;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      removeHoverPopup();
      removePinnedPopup();
      listeners.splice(0).forEach(([event, layerOrHandler, maybeHandler]) => {
        if (maybeHandler) map.off(event, layerOrHandler, maybeHandler);
        else map.off(event, layerOrHandler);
      });
      map.remove();
    },
    setData(nextFeatureCollection) {
      if (destroyed) return;
      removeHoverPopup();
      removePinnedPopup();
      map.getSource(COUNTY_SOURCE_ID)?.setData(nextFeatureCollection);
    },
    setLanguage(nextLanguage) {
      if (destroyed) return;
      currentLanguage = nextLanguage === "ES" ? "ES" : "EN";
      element.setAttribute("aria-label", communityReportCopy(currentLanguage, "county_map_aria"));
      if (hoverPopup && hoverDetails) {
        hoverPopup.setHTML(countyPopupMarkup(hoverDetails, popupOptions()));
      }
      if (pinnedPopup && pinnedDetails) {
        pinnedPopup.setHTML(countyPopupMarkup(pinnedDetails, popupOptions()));
      }
    },
    setThresholds(nextThresholds) {
      if (destroyed) return;
      map.setPaintProperty(
        COUNTY_FILL_LAYER_ID,
        "fill-color",
        countyFillColorExpression(nextThresholds, theme),
      );
    },
  });
}

export function escapeCountyHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function waitForInitialStyle(map) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => map.off?.("error", fail);
    const ready = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const fail = (event) => {
      if (settled) return;
      settled = true;
      cleanup();
      map.remove();
      reject(event?.error || new Error("Mapbox style failed to load."));
    };
    map.once("error", fail);
    map.once("style.load", ready);
  });
}

function numericFeatureCount(value) {
  const count = Number(value);
  return Number.isInteger(count) && count >= 0 ? count : 0;
}
